/**
 * XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX
 *
 * THIS FILE IS MANAGED CENTRALLY BY THE `common-code` REPO.
 * IT COULD BE AUTO-REPLACED AT ANY TIME.
 * DO NOT MAKE CUSTOM CHANGES TO THIS FILE.
 * @see https://gitlab.com/dea-aero/development/common-code
 *
 * XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX
 */
import {create as zcreate} from 'zustand';
import {get, set, del} from 'idb-keyval';
import {createJSONStorage, persist} from 'zustand/middleware';
import type {StoreMutatorIdentifier} from "zustand/vanilla";
import type {StateCreator} from "zustand/vanilla";
import type {Mutate} from "zustand/vanilla";
import type {StoreApi} from "zustand/vanilla";
// @ts-ignore
import {UseBoundStore} from "zustand/react";
import {PersistOptions} from 'zustand/middleware';


/**
 * We've got a TSX file here to allow us to use the Zustand library in a centralised and simplified way, while
 * preserving the type-hinting that the original library provides.
 * This allows tools such as PyCharm to understand the nature of arguments being passed to the CreateStore are
 * translated into objects which are callable and usable throughout the project.
 */
export const createDefaultJsonStorage = () => createJSONStorage(() => ({
    // Retrieves the state string from IndexedDB
    getItem: async (name: string) => await get(name),

    // Saves the state string to IndexedDB
    setItem: async (name: string, value: any) => {
        // prevent saving the object with nothing in it
        if (value === '{"version":0}') return;
        if (value.length > 100000) console.log('saving large item to IDB:', name, value.length, 'bytes')
        await set(name, value)
    },

    // Removes the state from IndexedDB
    removeItem: async (name: string) => await del(name),
}))

const hasData = (fields: [], state: {}): boolean => {
    /**
     * If none of the state object fields listed in the 'fields' list contain any data, then return false.
     */
    return fields.some(f => {
        if (typeof state[f] === 'object' && Object.keys(state[f]).length === 0) {
            return false;
        }
        // @ts-ignore
        if (Array.isArray(state[f]) && state[f].length === 0) {
            return false;
        }
        // noinspection RedundantIfStatementJS
        if (state[f] === '{}' || state[f] === '[]' || state[f] === '') {
            return false;
        }
        return true;
    });
}

export const createPersistStore = ({name, fields, store, every = 0}) => {
    /**
     *
     */
    let __lastSavedAt = 0;
    return {
        name,
        storage: createDefaultJsonStorage(),
        partialize: state => {
            if (every) {

                const _now = Date.now();
                if (_now - __lastSavedAt < every) {

                    if (!hasData(fields, state)) {
                        console.log(name, 'clearing state from IndexedDB…')
                    } else {
                        // dont save more frequently than every 5 seconds
                        return;
                    }
                }
                __lastSavedAt = _now;
            }

            const _save = {};
            fields.forEach(f => _save[f] = state[f]);

            console.log(name, 'Saving state to iDB…', _save);
            return _save;
        },
        onRehydrateStorage: () => {
            console.log(name, 'Hydrating state from IndexedDB…');
            return (state, error) => {
                if (error) {
                    console.error(name, 'Hydration error:', error);
                } else {
                    console.log(name, 'Hydration finished for store:', state);
                    const _load = {};
                    fields.forEach(f => _load[f] = state[f]);
                    store().setState(_load);
                }
            };
        },
    }
}

type CreateStore = {
    <T, Mos extends [StoreMutatorIdentifier, unknown][] = []>(initializer: StateCreator<T, [], Mos>): UseBoundStore<Mutate<StoreApi<T>, Mos>>;
    <T>(): <Mos extends [StoreMutatorIdentifier, unknown][] = []>(initializer: StateCreator<T, [], Mos>) => UseBoundStore<Mutate<StoreApi<T>, Mos>>;
    <S extends StoreApi<unknown>>(store: S): UseBoundStore<S>;
};

export const createStore = (
    (createOpts: StateCreator<any, any, any>, persistOpts: PersistOptions<any> = null) => {
        if (persistOpts !== null)
            return zcreate(persist(createOpts, persistOpts))
        return zcreate(createOpts);
    }
) as CreateStore;
